import {
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  Input,
  Skeleton,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react'
import type { OrganizationUnit } from 'BrokerTsLib/src/dtos'
import {
  useDeleteLogo,
  useUploadLogo,
} from 'BrokerTsLib/src/hooks/useOrganizationUnit'
import { FormProvider, useForm } from 'BrokerTsLib/src/rhf'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'

import { useAuth } from '@/context/AuthContext'
import { Building, Group, Trash, Upload } from '@/icons'

import { Address } from '../../components/Address'
import { EmailAddress } from '../../components/EmailAddress'
import { Phone } from '../../components/Phone'
import { ValidityBadge } from '../../components/ValidityBadge'
import { OrganizationTypeBadge } from './OrganizationTypeBadge'

export const OrganizationHeader = ({
  organization,
  isLoading,
  children,
}: {
  organization?: OrganizationUnit
  isLoading?: boolean
  children?: ReactNode
}) => {
  const auth = useAuth()
  const [logoUrl, setLogoUrl] = useState<string | undefined>(
    organization?.logoPartner,
  )

  useEffect(() => {
    setLogoUrl(organization?.logoPartner)
  }, [organization?.logoPartner])

  const isCurrentOrganization = auth?.user?.ouId === organization?.id

  const absLogoUrl = `${import.meta.env.VITE_BASE_URL}/${logoUrl}`

  return (
    <OrganizationHeaderLayout
      avatar={
        <Box position="relative">
          {isCurrentOrganization && <CurrentOrganizationBadge />}
          <Skeleton isLoaded={!isLoading} borderRadius={8} role="group">
            <Avatar
              size={{base: 'lg', md: '2xl'}}
              colorScheme="gray"
              ignoreFallback={true}
              icon={
                <Group boxSize={{ base: '12', md: '20' }} color="primary" />
              }
              src={logoUrl && absLogoUrl}
              borderRadius={'md'}
              p={0.5}
              sx={{
                '> img': {
                  objectFit: 'contain',
                },
              }}
            />
            {organization && auth?.user?.allowEditOrganizationStructure && (
              <Box
                display="none"
                _groupHover={{ display: 'block' }}
                background="whiteAlpha.600"
                position="absolute"
                top={0}
                left={0}
                bottom={0}
                right={0}
                alignContent="center"
              >
                <LogoUploadForm
                  organizationUnitId={organization?.id}
                  logoUrl={logoUrl}
                  onUploaded={setLogoUrl}
                />
              </Box>
            )}
          </Skeleton>
        </Box>
      }
      title={
        <Flex
          sx={{
            flexDir: { base: 'column', md: 'row' },
            alignItems: { base: 'start', md: 'center' },
            flexWrap: 'wrap',
            rowGap: 1,
            columnGap: 3,
          }}
        >
          {organization?.name ? (
            <Text
              sx={{
                fontSize: { base: '2xl', md: '4xl' },
                fontWeight: 'semibold',
                color: 'white',
                alignItems: 'center',
                flexWrap: 'wrap',
                whiteSpace: 'pre-wrap',
              }}
            >
              {organization?.name}
            </Text>
          ) : (
            <Skeleton isLoaded={!isLoading} w={56} h={8} />
          )}
          <Flex gap={2} alignItems={'center'}>
            {organization?.valid != undefined && (
              <ValidityBadge valid={organization?.valid} />
            )}
            {organization?.type != undefined && (
              <OrganizationTypeBadge type={organization.type} />
            )}
          </Flex>
        </Flex>
      }
      info={
        <Stack color={'white'}>
          {organization?.email ? (
            <EmailAddress email={organization?.email} showIcon color="white" />
          ) : isLoading ? (
            <Skeleton isLoaded={!isLoading} w={48} h={6} />
          ) : null}

          {organization?.phone || organization?.mobilePhone ? (
            <Flex gap={2}>
              {organization?.phone && (
                <Phone number={organization?.phone} phoneIcon />
              )}
              {organization?.mobilePhone && (
                <Phone number={organization?.mobilePhone} mobilePhoneIcon />
              )}
            </Flex>
          ) : isLoading ? (
            <Skeleton isLoaded={!isLoading} w={48} h={6} />
          ) : null}

          {organization?.addressCity &&
            organization?.addressStreet &&
            organization?.addressStreetNumber &&
            organization?.addressPostCode ? (
              <Flex gap={2}>
                <Building />
                <Address
                  showIcon
                  city={organization?.addressCity}
                  street={organization?.addressStreet}
                  streetNumber={organization?.addressStreetNumber}
                  postCode={organization?.addressPostCode}
                />
              </Flex>
            ) : isLoading ? (
              <Skeleton isLoaded={!isLoading} w={56} h={6} />
            ) : null}
        </Stack>
      }
      buttons={children}
    />
  )
}
export const CurrentOrganizationBadge = () => {
  return (
    <Badge
      sx={{
        position: 'absolute',
        top: -2,
        left: -2,
        boxShadow: 'md',
        transform: 'rotate(-3deg)',
        zIndex: 1,
      }}
      backgroundColor="green.50"
      color="green.800"
      variant="solid"
      size={'lg'}
      fontSize={'md'}
    >
      Aktuální
    </Badge>
  )
}

const LogoUploadForm = ({
  organizationUnitId,
  logoUrl,
  onUploaded,
}: {
  organizationUnitId: number
  logoUrl?: string
  onUploaded: (url: string | undefined) => void
}) => {
  const toast = useToast()
  const methods = useForm<{ file: FileList }>()
  const { register, handleSubmit, getValues, reset } = methods

  const { mutateAsync: uploadLogoAsync, isLoading: isUploadLoading } =
    useUploadLogo()
  const { mutateAsync: deleteLogoAsync, isLoading: isDeleteLoading } =
    useDeleteLogo()

  const upload = async (values: { file: FileList }) => {
    const formData = new FormData()
    formData.append('file', values.file.item(0) as File)

    await uploadLogoAsync({ formData, ouId: organizationUnitId })
      .then((result) => {
        onUploaded(result.data + '?' + new Date().getTime())
      })
      .catch((e) =>
        toast({
          title: 'Nahrání loga se nezdařilo',
          status: 'error',
        }),
      )

    reset()
  }

  const deleteLogo = async () => {
    await deleteLogoAsync(organizationUnitId)
      .then(() => onUploaded(undefined))
      .catch((e) =>
        toast({
          title: 'Smazání loga se nezdařilo',
          status: 'error',
        }),
      )
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(upload)}>
        <Flex alignItems="center" h="full" flexDirection="column" gap={2}>
          <Input
            id="fileInput"
            display={'none'}
            type="file"
            {...register('file', { onChange: () => upload(getValues()) })}
            multiple={false}
            accept=".jpg, .png, .jpeg, .gif, .bmp, .tif, .svg, .tiff|image/*"
          />
          <Button
            type="button"
            variant="outline"
            py={2}
            onClick={() => document.getElementById('fileInput')?.click()}
            isLoading={isUploadLoading}
          >
            <Upload color="secondary.500" />
          </Button>
          {logoUrl && (
            <Button
              type="button"
              variant="outline"
              py={2}
              colorScheme="red"
              onClick={deleteLogo}
              isLoading={isDeleteLoading}
            >
              <Trash color="red.500" />
            </Button>
          )}
        </Flex>
      </form>
    </FormProvider>
  )
}

const OrganizationHeaderLayout = ({
  avatar,
  title,
  info,
  buttons,
}: {
  avatar: ReactNode
  title: ReactNode
  info: ReactNode
  buttons: ReactNode
}) => (
  <Box bg="dashboard-header-bg">
    <Container as={Stack} gap={4} py={{base: 4, md: 8}} pt={{base: 6, md: 8}} color="white">
      <Flex
        flexDirection={{ base: 'column', sm: 'row' }}
        justifyContent="space-between"
        gap={4}
      >
        <Grid
          templateColumns="auto 1fr"
          templateRows="auto auto"
          columnGap={{base: 4, md: 6}}
          rowGap={{base: 4, md: 2}}
        >
          <GridItem rowSpan={{ base: 1, sm: 2 }}>{avatar}</GridItem>
          <GridItem alignContent={{base: 'center', md: 'start'}}>{title}</GridItem>
          <GridItem colSpan={{ base: 2, sm: 1 }}>{info}</GridItem>
        </Grid>
        <Flex
          sx={{
            alignItems: 'end',
            flexDir: 'row',
            gap: 2,
          }}
        >
          {buttons}
        </Flex>
      </Flex>
    </Container>
  </Box>
)
